import React, { useState, useEffect } from 'react';
import { useAccount } from 'wagmi';
import { ethers } from 'ethers';
import { Spinner, Card, Button, Badge, Row, Col, Container } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FaWallet, FaImages, FaSync, FaArrowRight } from 'react-icons/fa';
import 'bootstrap/dist/css/bootstrap.min.css';
import { ConnectButton } from '@rainbow-me/rainbowkit';

// ERC-721 ABI with ERC721Enumerable functions
const ERC721_ABI = [
  'function balanceOf(address owner) view returns (uint256)',
  'function ownerOf(uint256 tokenId) view returns (address)',
  'function tokenURI(uint256 tokenId) view returns (string)',
  'function tokenOfOwnerByIndex(address owner, uint256 index) view returns (uint256)',
  'function totalSupply() view returns (uint256)',
  'function tokenByIndex(uint256 index) view returns (uint256)',
  'function isPrivate(uint256 tokenId) view returns (bool)'
];

const CONTRACT_ADDRESS = '0x0840DF63b08a2A9b80D820e803bf9c820fea9772';

interface NFT {
  id: string;
  name: string;
  description: string;
  image: string;
  ipfsGateways?: string[];
  attributes: Array<{
    trait_type: string;
    value: string | number;
  }>;
  imageLoadError?: boolean;
}

const NFTGallery: React.FC = () => {
  const navigate = useNavigate();
  const { address, isConnected } = useAccount();
  const [nfts, setNfts] = useState<NFT[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resolveIPFS = (url: string): { url: string; gateways: string[] } => {
    if (!url) return { url: '', gateways: [] };
    
    if (url.startsWith('http')) {
      if (url.includes('pinata.cloud/ipfs/')) {
        return { url, gateways: [url] };
      }
      return { url, gateways: [url, ...getDefaultGateways(url)] };
    }
    
    const gateways = [
      'https://tomato-accurate-rabbit-784.mypinata.cloud/ipfs/',
      'https://ipfs.io/ipfs/',
      'https://cloudflare-ipfs.com/ipfs/',
      'https://gateway.pinata.cloud/ipfs/',
      'https://dweb.link/ipfs/'
    ];
    
    let ipfsHash = '';
    if (url.startsWith('ipfs://ipfs/')) {
      ipfsHash = url.split('ipfs://ipfs/')[1];
    } else if (url.startsWith('ipfs://')) {
      ipfsHash = url.split('ipfs://')[1];
    } else if (url.startsWith('Qm') && url.length === 46) {
      ipfsHash = url;
    } else if (url.includes('ipfs/')) {
      const parts = url.split('ipfs/');
      ipfsHash = parts[parts.length - 1];
    } else {
      return { url, gateways: [url] };
    }
    
    const gatewayUrls = gateways.map(gateway => gateway + ipfsHash);
    return { url: gatewayUrls[0], gateways: [...new Set(gatewayUrls)] };
  };

  const getDefaultGateways = (url: string): string[] => {
    const ipfsMatch = url.match(/ipfs\/([^/]+)/);
    if (!ipfsMatch) return [];
    
    const ipfsHash = ipfsMatch[1];
    return [
      `https://tomato-accurate-rabbit-784.mypinata.cloud/ipfs/${ipfsHash}`,
      `https://ipfs.io/ipfs/${ipfsHash}`,
      `https://cloudflare-ipfs.com/ipfs/${ipfsHash}`,
      `https://gateway.pinata.cloud/ipfs/${ipfsHash}`,
      `https://dweb.link/ipfs/${ipfsHash}`
    ];
  };

  const fetchNFTs = async () => {
    if (!isConnected || !address) {
      setError('Please connect your wallet first');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const provider = new ethers.providers.JsonRpcProvider('https://bsc-dataseed.binance.org/');
      const contract = new ethers.Contract(CONTRACT_ADDRESS, ERC721_ABI, provider);

      const balance = await contract.balanceOf(address);
      
      if (balance.eq(0)) {
        setNfts([]);
        return;
      }

      const tokenIds = [];
      for (let i = 0; i < balance.toNumber(); i++) {
        try {
          const tokenId = await contract.tokenOfOwnerByIndex(address, i);
          tokenIds.push(tokenId);
        } catch (err) {
          console.warn(`Error getting token at index ${i}:`, err);
          break;
        }
      }

      const nftResults = [];
      for (const tokenId of tokenIds) {
        try {
          const tokenIdStr = tokenId.toString();
          let tokenURI;
          let metadataUrl;
          
          try {
            tokenURI = await contract.tokenURI(tokenId);
            const isPrivate = await contract.isPrivate(tokenId).catch(() => false);
            
            if (isPrivate) {
              continue;
            }
            
            metadataUrl = resolveIPFS(tokenURI);
            
            if (!metadataUrl || !metadataUrl.url.startsWith('http')) {
              metadataUrl = `https://ipfs.io/ipfs/${tokenIdStr}`;
            }
            
          } catch (uriError) {
            metadataUrl = `https://ipfs.io/ipfs/${tokenIdStr}`;
          }
          
          let cid = '';
          const cidMatch = tokenURI?.match(/ipfs\/([^/\?]+)/);
          cid = cidMatch?.[1] || tokenIdStr;

          const gateways = [
            tokenURI?.startsWith('http') ? tokenURI : `https://tomato-accurate-rabbit-784.mypinata.cloud/ipfs/${cid}`,
            `https://tomato-accurate-rabbit-784.mypinata.cloud/ipfs/${cid}`,
            `https://gateway.pinata.cloud/ipfs/${cid}`,
            `https://ipfs.io/ipfs/${cid}`,
            `https://cloudflare-ipfs.com/ipfs/${cid}`,
            `https://dweb.link/ipfs/${cid}`
          ].filter((url, index, self) => 
            url && url.startsWith('http') && self.indexOf(url) === index
          );
          
          let metadata = null;
          for (const gateway of gateways) {
            try {
              const response = await fetch(gateway);
              if (response.ok) {
                metadata = await response.json();
                break;
              }
            } catch (err) {
              continue;
            }
          }
          
          if (metadata) {
            const imageSource = metadata.image_url || metadata.image;
            if (imageSource) {
              const { url: imageUrl, gateways: imageGateways } = resolveIPFS(imageSource);
              metadata.image = imageUrl;
              metadata.ipfsGateways = imageGateways;
            }
            
            nftResults.push({
              id: tokenIdStr,
              name: metadata.name || `NFT #${tokenIdStr}`,
              description: metadata.description || '',
              image: metadata.image || '',
              attributes: metadata.attributes || [],
              ipfsGateways: metadata.ipfsGateways || []
            });
          }
          
        } catch (err) {
          console.error(`Error processing token ${tokenId}:`, err);
        }
      }

      setNfts(nftResults);
      
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setError(`Failed to load NFTs: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isConnected) {
      fetchNFTs();
    }
  }, [isConnected, address]);

  // Loading state
  if (loading) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '60vh' }}>
        <Spinner 
          animation="border" 
          role="status" 
          style={{ width: '3rem', height: '3rem' }}
          className="mb-3"
        />
        <h4 className="mb-3">Loading your NFTs</h4>
        <p className="text-muted">This may take a moment...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <Container className="my-5">
        <div className="alert alert-warning">
          <h5>Error Loading NFTs</h5>
          <div className="mb-2">{error}</div>
          <Button 
            variant="outline-primary" 
            onClick={fetchNFTs}
            className="mt-2"
          >
            <FaSync className="me-2" />
            Try Again
          </Button>
        </div>
      </Container>
    );
  }

  // Not connected state
  if (!isConnected) {
    return (
      <Container className="my-5">
        <Row className="justify-content-center">
          <Col xs={12} md={8} lg={6}>
            <Card className="shadow-sm border-0">
              <Card.Body className="text-center p-5">
                <div className="mb-4">
                  <div className="d-inline-flex align-items-center justify-content-center bg-primary bg-opacity-10 rounded-circle" style={{ width: '80px', height: '80px' }}>
                    <FaWallet className="text-primary" size={32} />
                  </div>
                </div>
                <h3 className="h4 mb-3">Connect Your Wallet</h3>
                <p className="text-muted mb-4">
                  Connect your wallet to view your NFT collection and access exclusive features.
                </p>
                <div className="d-flex justify-content-center">
                  <ConnectButton 
                    label="Connect Wallet"
                    accountStatus="address"
                    chainStatus="icon"
                    showBalance={false}
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Empty state
  if (nfts.length === 0) {
    return (
      <Container className="my-5">
        <Row className="justify-content-center">
          <Col xs={12} md={8} lg={6} className="text-center">
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-5">
                <div className="mb-4">
                  <div className="d-inline-flex align-items-center justify-content-center bg-light rounded-circle" style={{ width: '100px', height: '100px' }}>
                    <FaImages className="text-muted" size={48} />
                  </div>
                </div>
                <h3 className="h4 mb-3">No NFTs Found</h3>
                <p className="text-muted mb-4">
                  You don't have any NFTs in your wallet yet. Start collecting to see them here!
                </p>
                <Button 
                  variant="primary" 
                  onClick={fetchNFTs}
                  className="px-4"
                >
                  <FaSync className="me-2" />
                  Refresh
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid className="px-0 px-md-3 py-4">
      <Container className="px-0 px-md-3">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="h3 mb-0">My Collection</h2>
          <Button 
            variant="outline-secondary" 
            size="sm"
            onClick={fetchNFTs}
            className="d-flex align-items-center"
          >
            <FaSync className="me-1" />
            <span className="d-none d-sm-inline">Refresh</span>
          </Button>
        </div>
        
        <Row className="g-3 g-md-4">
          {nfts.map((nft) => (
            <Col key={nft.id} xs={12} sm={6} md={4} lg={3}>
              <Card 
                className="h-100 shadow-sm border-0 rounded-3 overflow-hidden"
                onClick={() => navigate(`/nft/${nft.id}`)}
                style={{
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 10px 20px rgba(0,0,0,0.1)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
                }}
              >
                <div style={{ 
                  position: 'relative', 
                  paddingTop: '100%',
                  backgroundColor: '#f8f9fa',
                  overflow: 'hidden'
                }}>
                  {nft.image ? (
                    <img
                      src={nft.image}
                      alt={nft.name}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.3s ease'
                      }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = 'https://via.placeholder.com/400x400?text=No+Image';
                      }}
                    />
                  ) : (
                    <div className="position-absolute w-100 h-100 d-flex flex-column align-items-center justify-content-center">
                      <FaImages className="text-muted mb-2" size={32} />
                      <span className="text-muted small">No image available</span>
                    </div>
                  )}
                </div>
                <Card.Body className="p-3">
                  <h5 className="card-title mb-1 text-truncate">{nft.name}</h5>
                  <p 
                    className="card-text small text-muted mb-2" 
                    style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      minHeight: '2.5rem'
                    }}
                  >
                    {nft.description || 'No description available'}
                  </p>
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="badge bg-primary">
                      #{nft.id}
                    </span>
                    <Button 
                      variant="outline-primary" 
                      size="sm"
                      className="d-flex align-items-center"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/nft/${nft.id}`);
                      }}
                    >
                      <span className="d-none d-sm-inline me-1">View</span>
                      <FaArrowRight size={12} />
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </Container>
      
      <style jsx global>{`
        @media (max-width: 767.98px) {
          .container {
            padding-left: 1rem;
            padding-right: 1rem;
          }
          
          .card {
            border-radius: 12px !important;
          }
        }
        
        @media (min-width: 768px) {
          .card {
            border-radius: 12px;
          }
        }
        
        .card:hover {
          transform: translateY(-4px) !important;
          box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
        }
      `}</style>
    </Container>
  );
};

export default NFTGallery;
